import type { LocalizedDict } from '../index'

// Admin Settings → Etsy listing push → "Pushes" tab, the store switch-on
// confirmation and the go-live checklist
// (features/admin/etsy-listing/EtsyPushesTab.tsx, EtsyGoLiveCard.tsx).
// Plan P5, mockup approved by Volkan 2026-10-27. "Etsy", "Pexify" are names.

export type EtsyPushAdminStrings = {
  kpi: {
    total: (days: number) => string
    totalAll: string
    sent: string
    attention: string
    failed: string
  }
  filters: {
    search: string
    result: string
    results: Record<'attention' | 'all' | 'sent' | 'partial' | 'uncertain' | 'failed' | 'running', string>
    store: string
    allStores: string
    window: string
    days: (n: number) => string
    allTime: string
  }
  table: {
    when: string
    seller: string
    product: string
    steps: string
    result: string
    noListing: string
    unknownListing: string
    stepsLabel: (done: number, total: number) => string
    legacy: string
  }
  status: Record<string, string>
  rowHint: string
  empty: string
  detail: {
    pick: string
    pickHint: string
    sentBy: (name: string) => string
    seconds: (n: number) => string
    openOnEtsy: string
    listingNo: (id: string) => string
    sentSummary: string
    summary: (rows: number, onSale: number, photos: number) => string
    priceRange: (min: string, max: string) => string
    retry: string
    retrying: string
    retryHint: string
    retried: string
    retryFailed: string
    uncertainTitle: string
    uncertainBody: string
    uncertainReminder: string
    raw: string
    log: string
    legacy: string
    loadError: string
  }
  events: Record<string, string>
  stores: {
    count30d: string
    counts: (n: number, ok: number) => string
    testTag: string
    blockedHint: string
  }
  confirm: {
    title: (shop: string) => string
    draftsOnly: string
    noCharge: string
    canTurnOff: string
    notChecked: string
    testStoreNote: string
    cancel: string
    turnOn: string
  }
  goLive: {
    title: string
    hint: string
    codeLive: string
    codeLiveDetail: string
    testStore: string
    testStoreDetail: (shops: string) => string
    testStoreTodo: string
    endToEnd: string
    endToEndDetail: (title: string) => string
    endToEndTodo: string
    result: string
    check: string
    checkDetail: string
    checkedBy: (name: string, when: string) => string
    checkButton: string
    checkFailed: string
    sellers: string
    sellersDetail: (shops: string) => string
    sellersTodo: string
  }
}

export const etsyPushAdminI18n: LocalizedDict<EtsyPushAdminStrings> = {
  en: {
    kpi: {
      total: (days) => `Sends in the last ${days} days`,
      totalAll: 'All sends',
      sent: 'Completed',
      attention: 'Needs attention · step missing / uncertain',
      failed: 'Etsy didn’t accept · no listing created',
    },
    filters: {
      search: 'Search product, seller or Etsy listing no.',
      result: 'Result',
      results: {
        attention: 'Needs attention',
        all: 'All',
        sent: 'Completed',
        partial: 'Step missing',
        uncertain: 'Uncertain',
        failed: 'Not accepted',
        running: 'Sending',
      },
      store: 'Store',
      allStores: 'All stores',
      window: 'Period',
      days: (n) => `Last ${n} days`,
      allTime: 'All time',
    },
    table: {
      when: 'When',
      seller: 'Seller · Store',
      product: 'Product',
      steps: 'Steps',
      result: 'Result',
      noListing: 'no listing created',
      unknownListing: 'listing no. unknown',
      stepsLabel: (done, total) => `${done} of ${total} steps done`,
      legacy: 'older push',
    },
    status: {
      pending: 'Queued',
      in_progress: 'Sending',
      sent: 'Completed',
      partial: 'Step missing',
      uncertain: 'Uncertain · waiting for the seller',
      failed: 'Not accepted',
      dead_letter: 'Not accepted',
    },
    rowHint:
      'Click a row to see the result step by step. Step dots in order: listing · photos · options · colour photos · personalization · check and match.',
    empty: 'No sends match these filters.',
    detail: {
      pick: 'Pick a send',
      pickHint: 'Step results, Etsy’s reply and what can be done show here.',
      sentBy: (name) => `sent by: ${name}`,
      seconds: (n) => `${n} s`,
      openOnEtsy: 'Open on Etsy ↗',
      listingNo: (id) => `listing no. ${id}`,
      sentSummary: 'What was sent',
      summary: (rows, onSale, photos) =>
        `${rows} option rows · ${onSale} on sale · ${photos} ${photos === 1 ? 'photo' : 'photos'}`,
      priceRange: (min, max) => (min === max ? `$${min}` : `$${min} – $${max}`),
      retry: 'Retry the missing steps',
      retrying: 'Retrying…',
      retryHint:
        'Only the missing step goes to the same Etsy draft; no new listing is created. The seller sees the same button on their screen.',
      retried: 'The missing steps are being sent again',
      retryFailed: 'Couldn’t retry',
      uncertainTitle: 'Etsy didn’t answer in time',
      uncertainBody:
        'The listing may have been created. To avoid a duplicate there is no retry here; the decision is the seller’s. On their screen they either link the draft they find or say “no listing, send again”.',
      uncertainReminder:
        'If the seller doesn’t decide within 24 hours this stays under “Needs attention” and the seller gets a reminder.',
      raw: 'Etsy’s raw reply',
      log: 'Event log',
      legacy: 'An older push (before the new send flow): no step results.',
      loadError: 'Couldn’t load this send',
    },
    events: {
      started: 'Send started',
      retried: 'Missing steps sent again',
      resolved: 'Seller resolved the uncertain send',
      created: 'Etsy draft created',
      step_failed: 'Step failed',
      uncertain: 'Etsy didn’t answer — marked uncertain',
      failed: 'Etsy didn’t accept',
      completed: 'Finished',
      uncertain_reminder: 'Reminder sent to the seller',
    },
    stores: {
      count30d: 'Sends (30 days)',
      counts: (n, ok) => `${n} · ${ok} done`,
      testTag: 'test',
      blockedHint: 'A store missing Etsy permissions can’t be switched on; it opens once the seller reconnects it.',
    },
    confirm: {
      title: (shop) => `Turn on Etsy sending for ${shop}?`,
      draftsOnly: 'The seller and their team can send catalog products to this store as drafts only; publishing happens on Etsy.',
      noCharge: 'Nothing is charged; the listing fee is charged when the seller publishes on Etsy.',
      canTurnOff: 'You can turn it off any time; drafts already created stay on Etsy.',
      notChecked: 'The test step isn’t confirmed yet (Go-live · 4). You can still turn it on.',
      testStoreNote: 'A store turned on before that counts as a test store.',
      cancel: 'Cancel',
      turnOn: 'Turn on sending',
    },
    goLive: {
      title: 'Go-live',
      hint: 'In order, before opening to sellers. Steps tick themselves; you confirm step 4.',
      codeLive: 'Code is live',
      codeLiveDetail: 'Each store stays off until it is turned on here',
      testStore: 'Turn on a test store',
      testStoreDetail: (shops) => `${shops} on`,
      testStoreTodo: 'Turn on one of our own stores in the list',
      endToEnd: 'Send one product end to end',
      endToEndDetail: (title) => `${title} · all steps done`,
      endToEndTodo: 'From that store’s seller screen: List in my store → Send to Etsy',
      result: 'Result',
      check: 'Check the draft on Etsy',
      checkDetail: 'Photos, options, prices, SKUs right on Etsy?',
      checkedBy: (name, when) => `${name} · ${when}`,
      checkButton: 'I checked it',
      checkFailed: 'Couldn’t save the check',
      sellers: 'Turn on chosen seller stores',
      sellersDetail: (shops) => `${shops} on`,
      sellersTodo: 'From the store list, store by store',
    },
  },
  tr: {
    kpi: {
      total: (days) => `Son ${days} günde gönderim`,
      totalAll: 'Tüm gönderimler',
      sent: 'Tamamlandı',
      attention: 'İlgilenilmeli · eksik adım / belirsiz',
      failed: 'Etsy kabul etmedi · ilan açılmadı',
    },
    filters: {
      search: 'Ürün, satıcı veya Etsy ilan no. ara',
      result: 'Sonuç',
      results: {
        attention: 'İlgilenilmeli',
        all: 'Tümü',
        sent: 'Tamamlandı',
        partial: 'Eksik adım',
        uncertain: 'Belirsiz',
        failed: 'Kabul edilmedi',
        running: 'Gönderiliyor',
      },
      store: 'Mağaza',
      allStores: 'Tüm mağazalar',
      window: 'Zaman',
      days: (n) => `Son ${n} gün`,
      allTime: 'Tümü',
    },
    table: {
      when: 'Zaman',
      seller: 'Satıcı · Mağaza',
      product: 'Ürün',
      steps: 'Adımlar',
      result: 'Sonuç',
      noListing: 'ilan açılmadı',
      unknownListing: 'ilan no. bilinmiyor',
      stepsLabel: (done, total) => `${done}/${total} adım tamam`,
      legacy: 'eski gönderim',
    },
    status: {
      pending: 'Sırada',
      in_progress: 'Gönderiliyor',
      sent: 'Tamamlandı',
      partial: 'Eksik adım',
      uncertain: 'Belirsiz · satıcı bekleniyor',
      failed: 'Kabul edilmedi',
      dead_letter: 'Kabul edilmedi',
    },
    rowHint:
      'Satıra tıklayınca adım adım sonuç açılır. Adım noktaları sırası: ilan · fotoğraflar · seçenekler · renk fotoğrafları · kişiselleştirme · kontrol ve eşleme.',
    empty: 'Bu filtrelere uyan gönderim yok.',
    detail: {
      pick: 'Bir gönderim seçin',
      pickHint: 'Adım sonuçları, Etsy’nin cevabı ve yapılabilecek işlem burada görünür.',
      sentBy: (name) => `gönderen: ${name}`,
      seconds: (n) => `${n} sn`,
      openOnEtsy: 'Etsy’de aç ↗',
      listingNo: (id) => `ilan no. ${id}`,
      sentSummary: 'Gönderilen',
      summary: (rows, onSale, photos) => `${rows} seçenek satırı · ${onSale} satışta · ${photos} fotoğraf`,
      priceRange: (min, max) => (min === max ? `${min} $` : `${min} – ${max} $`),
      retry: 'Eksikleri yeniden dene',
      retrying: 'Yeniden deneniyor…',
      retryHint:
        'Yalnız eksik adım aynı Etsy taslağına gider; yeni ilan açılmaz. Satıcı da kendi ekranından aynı düğmeyi görür.',
      retried: 'Eksik adımlar yeniden gönderiliyor',
      retryFailed: 'Yeniden denenemedi',
      uncertainTitle: 'Etsy zamanında cevap vermedi',
      uncertainBody:
        'İlan açılmış olabilir. Çift ilan olmasın diye burada yeniden deneme yok; karar satıcının. Satıcı kendi ekranında ya bulduğu taslağı bağlar ya da “ilan yok, yeniden gönder” der.',
      uncertainReminder:
        'Satıcı 24 saat içinde karar vermezse bu satır “İlgilenilmeli” listesinde kalır; satıcıya hatırlatma bildirimi gider.',
      raw: 'Etsy’nin ham cevabı',
      log: 'Olay kaydı',
      legacy: 'Eski bir gönderim (yeni gönderim akışından önce): adım sonucu yok.',
      loadError: 'Bu gönderim yüklenemedi',
    },
    events: {
      started: 'Gönderim başladı',
      retried: 'Eksik adımlar yeniden gönderildi',
      resolved: 'Satıcı belirsiz gönderimi çözdü',
      created: 'Etsy taslağı açıldı',
      step_failed: 'Adım başarısız',
      uncertain: 'Etsy cevap vermedi — belirsiz olarak işaretlendi',
      failed: 'Etsy kabul etmedi',
      completed: 'Bitti',
      uncertain_reminder: 'Satıcıya hatırlatma gitti',
    },
    stores: {
      count30d: 'Gönderimler (30 gün)',
      counts: (n, ok) => `${n} · ${ok} tamam`,
      testTag: 'deneme',
      blockedHint: 'İzni eksik mağazada anahtar açılamaz; satıcı mağazasını yeniden bağlayınca açılır.',
    },
    confirm: {
      title: (shop) => `${shop}’da Etsy gönderimi açılsın mı?`,
      draftsOnly: 'Satıcı ve ekibi katalogdan bu mağazaya yalnız taslak ilan gönderebilir; yayın Etsy’de yapılır.',
      noCharge: 'Ücret kesilmez; ilan ücreti satıcı Etsy’de yayınlayınca kesilir.',
      canTurnOff: 'İstediğiniz an kapatabilirsiniz; açılmış taslaklar Etsy’de kalır.',
      notChecked: 'Deneme adımı henüz onaylanmadı (Canlıya açılış · 4). Yine de açabilirsiniz.',
      testStoreNote: 'Onaydan önce açılan mağaza deneme mağazası sayılır.',
      cancel: 'Vazgeç',
      turnOn: 'Gönderimi aç',
    },
    goLive: {
      title: 'Canlıya açılış',
      hint: 'Satıcılara açmadan önce sırayla. Adımlar kendiliğinden işaretlenir; 4. adımı siz onaylarsınız.',
      codeLive: 'Kod canlıda',
      codeLiveDetail: 'Her mağaza buradan açılana kadar kapalı kalır',
      testStore: 'Deneme mağazasında aç',
      testStoreDetail: (shops) => `${shops} açık`,
      testStoreTodo: 'Listeden kendi mağazalarımızdan birini açın',
      endToEnd: 'Bir ürünü uçtan uca gönder',
      endToEndDetail: (title) => `${title} · tüm adımlar tamam`,
      endToEndTodo: 'O mağazanın satıcı ekranından: Mağazama gönder → Etsy’ye gönder',
      result: 'Sonuç',
      check: 'Etsy’de taslağı kontrol et',
      checkDetail: 'Fotoğraflar, seçenekler, fiyatlar, SKU’lar Etsy’de doğru mu?',
      checkedBy: (name, when) => `${name} · ${when}`,
      checkButton: 'Kontrol ettim',
      checkFailed: 'Kontrol kaydedilemedi',
      sellers: 'Seçili satıcı mağazalarında aç',
      sellersDetail: (shops) => `${shops} açık`,
      sellersTodo: 'Mağazalar listesinden, mağaza mağaza',
    },
  },
}
